import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import React from 'react';

import { Button } from '@commons/utils/styledLibraryComponents';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import CustomDrawer from '@commons/Drawer';
import Footer from '@commons/Footer/Footer';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import {
  disconnectIntegration,
  getOauth2RedirectUrl,
  saveIntegrationMappings,
  saveIntegrationToken,
} from '@services/integrations';

import {
  COMBO_TOKEN_CODE,
  INTEGRATION_STATUSES,
  OAUTH2_AUTH_TYPE,
  PENNYLANE_OAUTH2_CODE,
  SKELLO_TOKEN_CODE,
  TOKEN_AUTH_TYPE,
  INTEGRATION_STATUS_LABEL_KEYS,
} from '../../utils/constants';
import {
  IntegrationStatusBadge,
  IntegrationStatusLabelHost,
} from '../IntegrationCard/styledComponents';

import {
  BodyScroll,
  BodyTitleHost,
  ButtonsContainer,
  MappingSectionHost,
  CloseButton,
  Divider,
  FeedbackMessageHost,
  FOOTER_BUTTON_STYLE,
  FOOTER_CUSTOM_STYLE,
  FOOTER_DEACTIVATE_BUTTON_STYLE,
  FOOTER_DEACTIVATE_ICON_STYLE,
  HeaderRow,
  IconWrapper,
  LogoAndTitle,
  LogoImage,
  OauthFooterHintBox,
  OauthFooterHintHost,
  OauthFooterHintText,
  PanelBodyBulletList,
  PanelBodyDescriptionHost,
  PanelRoot,
  PartnerNameHost,
  PartnerNameRow,
  PartnerTitleAndStatus,
  TokenFieldLabelHost,
  TokenInputField,
} from './styledComponents';
import { ExternalEntityMappingPanel } from './components/ExternalEntityMappingPanel';
import { PayrollSyncPanel } from './components/PayrollSyncPanel';

const TOKEN_ACTIVATION_INPULSE_ONLY_CODES = new Set([COMBO_TOKEN_CODE, SKELLO_TOKEN_CODE]);

const isInfoOnlyView = ({ code, isInpulseUser }) => {
  if (code === PENNYLANE_OAUTH2_CODE) {
    return false;
  }

  if (isInpulseUser && TOKEN_ACTIVATION_INPULSE_ONLY_CODES.has(code)) {
    return false;
  }

  return true;
};

const TokenFieldSection = ({ t, tokenInput, onTokenChange }) => (
  <TokenFieldLabelHost>
    <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
      {t('ADMIN.INTEGRATIONS.TOKEN_INTEGRATION_API_TOKEN_LABEL')}
    </Text>
    <TokenInputField
      autoComplete="off"
      spellCheck={false}
      type="text"
      value={tokenInput}
      onChange={onTokenChange}
    />
  </TokenFieldLabelHost>
);

const IntegrationDrawerHeader = ({ iconColor, iconLetter, logoUrl, name, status, t, onClose }) => (
  <HeaderRow>
    <LogoAndTitle>
      {logoUrl ? (
        <LogoImage alt="" src={logoUrl} />
      ) : (
        <IconWrapper $backgroundColor={iconColor}>
          <Text color={ENUM_COLORS.LIGHTEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14_WEIGHT_700_BOLD}>
            {iconLetter}
          </Text>
        </IconWrapper>
      )}
      <PartnerNameRow>
        <PartnerTitleAndStatus>
          <PartnerNameHost title={name}>
            <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H1_INTER}>
              {name}
            </Text>
          </PartnerNameHost>
          {status ? (
            <IntegrationStatusLabelHost>
              <IntegrationStatusBadge $status={status}>
                {t(INTEGRATION_STATUS_LABEL_KEYS[status])}
              </IntegrationStatusBadge>
            </IntegrationStatusLabelHost>
          ) : null}
        </PartnerTitleAndStatus>
      </PartnerNameRow>
    </LogoAndTitle>
    <CloseButton aria-label={t('GENERAL.CLOSE')} type="button" onClick={onClose}>
      <img alt="" src="/images/inpulse/close-black-small.svg" />
    </CloseButton>
  </HeaderRow>
);

const DrawerBodyFeedback = ({ error, mappingSaving, redirecting, tokenSaving, t }) => (
  <>
    {error ? (
      <FeedbackMessageHost>
        <Text color={ENUM_COLORS.INFO_RED} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
          {error}
        </Text>
      </FeedbackMessageHost>
    ) : null}
    {redirecting ? (
      <FeedbackMessageHost>
        <Text color={ENUM_COLORS.DARK} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
          {t('ADMIN.INTEGRATIONS.REDIRECTING')}
        </Text>
      </FeedbackMessageHost>
    ) : null}
    {tokenSaving ? (
      <FeedbackMessageHost>
        <Text color={ENUM_COLORS.DARK} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
          {t('ADMIN.INTEGRATIONS.TOKEN_SAVING')}
        </Text>
      </FeedbackMessageHost>
    ) : null}
    {mappingSaving ? (
      <FeedbackMessageHost>
        <Text color={ENUM_COLORS.DARK} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
          {t('GENERAL.LOADING')}
        </Text>
      </FeedbackMessageHost>
    ) : null}
  </>
);

const InfoOnlyContent = ({ integration, t }) => {
  const descriptionKey = integration?.description || '';
  const descriptionText = descriptionKey ? t(descriptionKey) : '';

  return (
    <PanelBodyDescriptionHost>
      {descriptionText ? (
        <>
          <Text color={ENUM_COLORS.IP_BLACK_1} font={ENUM_FONTS.TEXT_INTER_REGULAR_12_HEIGHT_20}>
            {descriptionText}
          </Text>
          <br />
        </>
      ) : null}
      <Text color={ENUM_COLORS.IP_BLACK_1} font={ENUM_FONTS.TEXT_INTER_REGULAR_12_HEIGHT_20}>
        {t('ADMIN.INTEGRATIONS.CONTACT_ACCOUNT_MANAGER')}
      </Text>
    </PanelBodyDescriptionHost>
  );
};

const IntegrationDescriptionContent = ({
  integration,
  isKitchenContext,
  isPaused,
  showInfoOnly,
  t,
  tokenInput,
  onTokenChange,
}) => {
  if (showInfoOnly) {
    return <InfoOnlyContent integration={integration} t={t} />;
  }

  const isTokenAuth = integration?.authType === TOKEN_AUTH_TYPE;
  const isActive = integration?.status === INTEGRATION_STATUSES.ACTIVE;
  const integrationCode = integration?.code || '';
  const panelTitleI18nKey =
    integrationCode === PENNYLANE_OAUTH2_CODE
      ? 'ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_PANEL_TITLE'
      : null;
  const panelTitleTranslated = panelTitleI18nKey ? t(panelTitleI18nKey) : '';
  const isPennyLaneKitchenDescription =
    integration?.code === PENNYLANE_OAUTH2_CODE && isKitchenContext;

  let panelBodyI18nKey = null;

  if (isTokenAuth) {
    panelBodyI18nKey = 'ADMIN.INTEGRATIONS.TOKEN_INTEGRATION_PANEL_DESCRIPTION';
  } else if (integrationCode === PENNYLANE_OAUTH2_CODE) {
    panelBodyI18nKey = isPennyLaneKitchenDescription
      ? 'ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_PANEL_DESCRIPTION_KITCHEN'
      : 'ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_PANEL_DESCRIPTION';
  }

  let panelBodyResult = '';
  let panelBodyIsArray = false;
  let panelBodyTranslated = '';

  if (panelBodyI18nKey) {
    if (isTokenAuth) {
      const partner = integration?.name ?? '';
      panelBodyResult = t(panelBodyI18nKey, { partner });
      panelBodyIsArray = false;
      panelBodyTranslated = panelBodyResult;
    } else {
      panelBodyResult = t(panelBodyI18nKey, { returnObjects: true });
      panelBodyIsArray = Array.isArray(panelBodyResult);
      panelBodyTranslated = panelBodyIsArray ? '' : panelBodyResult;
    }
  }

  const hasPanelBody =
    panelBodyI18nKey &&
    (panelBodyIsArray ? panelBodyResult.length > 0 : panelBodyResult !== panelBodyI18nKey);

  const showIntegrationTitle = Boolean(
    panelTitleI18nKey &&
      panelTitleTranslated !== panelTitleI18nKey &&
      (!isPaused || (isTokenAuth && isPaused)),
  );
  const showIntegrationDescriptionPanel =
    hasPanelBody && !isActive && (!isPaused || (isTokenAuth && isPaused));

  return (
    <>
      {showIntegrationTitle ? (
        <BodyTitleHost>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H2}>
            {panelTitleTranslated}
          </Text>
        </BodyTitleHost>
      ) : null}
      {showIntegrationDescriptionPanel ? (
        <PanelBodyDescriptionHost>
          {panelBodyIsArray ? (
            <PanelBodyBulletList>
              {panelBodyResult.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </PanelBodyBulletList>
          ) : (
            <Text color={ENUM_COLORS.IP_BLACK_1} font={ENUM_FONTS.TEXT_INTER_REGULAR_12_HEIGHT_20}>
              {panelBodyTranslated}
            </Text>
          )}
        </PanelBodyDescriptionHost>
      ) : null}
      {isTokenAuth ? (
        <TokenFieldSection
          integrationCode={integrationCode}
          t={t}
          tokenInput={tokenInput}
          onTokenChange={onTokenChange}
        />
      ) : null}
    </>
  );
};

const areMappingsEqual = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every(({ storeId, externalId }) =>
    b.some((m) => m.storeId === storeId && m.externalId === externalId),
  );
};

export const IntegrationDetailDrawer = ({
  integration,
  isInpulseUser = false,
  isKitchenContext = false,
  linkedStoreIds = null,
  openGenericModal,
  prefetchedMappings,
  prefetchedStores,
  returnPath,
  showErrorMessage,
  showSuccessMessage,
  storeId = null,
  onClose,
  onIntegrationDisconnected,
  onMappingsSaved,
  onTokenSaved,
}) => {
  const showInfoOnly = isInfoOnlyView({ code: integration?.code, isInpulseUser });
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenInput, setTokenInput] = useState(integration?.savedToken ?? '');
  const [tokenFromApi, setTokenFromApi] = useState(integration?.savedToken ?? '');
  const [disconnecting, setDisconnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [externalMappingLoadError, setExternalMappingLoadError] = useState(null);
  const [externalEntitiesRefreshKey, setExternalEntitiesRefreshKey] = useState(0);
  const [currentMappings, setCurrentMappings] = useState([]);
  const [isMappingValid, setIsMappingValid] = useState(true);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [savedMappingsSnapshot, setSavedMappingsSnapshot] = useState(null);

  const integrationCode = integration?.code || '';
  const isOAuth2 = integration?.authType === OAUTH2_AUTH_TYPE;
  const isTokenAuth = integration?.authType === TOKEN_AUTH_TYPE;
  const isComingSoon = integration?.status === INTEGRATION_STATUSES.COMING_SOON;
  const isActive = integration?.status === INTEGRATION_STATUSES.ACTIVE;
  const isPaused = integration?.status === INTEGRATION_STATUSES.PAUSED;

  const canActivate =
    !isComingSoon &&
    !isActive &&
    ((isOAuth2 && !isTokenAuth) || (isTokenAuth && tokenInput.trim().length > 0));

  const hasRequiredMappings = (integration?.requiredMappings?.length ?? 0) > 0;
  const showSaveButton = isTokenAuth || hasRequiredMappings;

  const showMappingActivePanel =
    !showInfoOnly &&
    isActive &&
    (integration?.code === PENNYLANE_OAUTH2_CODE || (isTokenAuth && hasRequiredMappings));

  const isHrTokenIntegration =
    integrationCode === COMBO_TOKEN_CODE || integrationCode === SKELLO_TOKEN_CODE;

  // Past payroll can only be re-synced on an active Combo/Skello integration (token present).
  const showPayrollSyncPanel = !showInfoOnly && isActive && isHrTokenIntegration;

  // Use the persisted mappings, not the live form state: a user can edit the mapping rows without
  // saving, and payroll must be synced for the stores that are actually mapped server-side.
  const payrollSyncStoreIds = useMemo(
    () => [...new Set((savedMappingsSnapshot ?? []).map(({ storeId }) => storeId))],
    [savedMappingsSnapshot],
  );

  const showOauthPausedFooterHint =
    isPaused && isOAuth2 && integration?.code === PENNYLANE_OAUTH2_CODE;

  useEffect(() => {
    const tokenUnchanged = !isTokenAuth || tokenInput.trim() === tokenFromApi.trim();
    const mappingsUnchanged =
      savedMappingsSnapshot === null || areMappingsEqual(currentMappings, savedMappingsSnapshot);
    const noChangesMade = tokenUnchanged && mappingsUnchanged;

    setSaveButtonDisabled(
      saving ||
        tokenSaving ||
        (isActive && !isMappingValid && externalMappingLoadError === null) ||
        (isTokenAuth && tokenInput.trim().length === 0) ||
        noChangesMade,
    );
  }, [
    saving,
    tokenSaving,
    isMappingValid,
    isActive,
    externalMappingLoadError,
    isTokenAuth,
    tokenInput,
    tokenFromApi,
    currentMappings,
    savedMappingsSnapshot,
  ]);

  useEffect(() => {
    setExternalMappingLoadError(null);
    setExternalEntitiesRefreshKey(0);
  }, [integration?.id]);

  const resetTransientState = useCallback(() => {
    setRedirecting(false);
    setTokenSaving(false);
    setTokenInput('');
    setDisconnecting(false);
    setSaving(false);
    setError(null);
    setExternalMappingLoadError(null);
    setExternalEntitiesRefreshKey(0);
    setCurrentMappings([]);
    setIsMappingValid(true);
    setSavedMappingsSnapshot(null);
  }, []);

  const handleClose = useCallback(() => {
    resetTransientState();
    onClose();
  }, [onClose, resetTransientState]);

  const submitTokenCredential = useCallback(
    async ({ keepOpenAfter }) => {
      if (!integration || !isTokenAuth || tokenSaving || saving) {
        return;
      }

      const trimmed = tokenInput.trim();

      if (trimmed.length === 0) {
        return;
      }

      setError(null);
      setTokenSaving(true);

      try {
        await saveIntegrationToken(integration.id, trimmed, storeId);
        setTokenSaving(false);

        await onTokenSaved();

        setExternalMappingLoadError(null);
        setExternalEntitiesRefreshKey((key) => key + 1);
      } catch {
        setTokenSaving(false);

        if (showErrorMessage) {
          showErrorMessage(t('ADMIN.INTEGRATIONS.TOKEN_CONNECT_ERROR'));
          return;
        }

        setError(t('ADMIN.INTEGRATIONS.TOKEN_CONNECT_ERROR'));
      }
    },
    [
      integration,
      isTokenAuth,
      onTokenSaved,
      saving,
      showErrorMessage,
      storeId,
      t,
      tokenInput,
      tokenSaving,
    ],
  );

  const handleSave = useCallback(async () => {
    const trimmed = (tokenInput || '').trim();
    const willSaveToken = isTokenAuth && trimmed.length > 0 && trimmed !== tokenFromApi.trim();
    const willSaveMappings = hasRequiredMappings && isMappingValid;

    setError(null);

    if (willSaveToken) {
      setTokenSaving(true);

      try {
        await saveIntegrationToken(integration.id, trimmed, storeId);
        setTokenFromApi(trimmed);

        onTokenSaved();
      } catch {
        setTokenSaving(false);

        if (showErrorMessage) {
          showErrorMessage(t('ADMIN.INTEGRATIONS.TOKEN_CONNECT_ERROR'));
          return;
        }

        setError(t('ADMIN.INTEGRATIONS.TOKEN_CONNECT_ERROR'));
        return;
      }

      setTokenSaving(false);
    }

    if (willSaveMappings) {
      setSaving(true);

      try {
        await saveIntegrationMappings(integration.id, currentMappings, storeId);
      } catch {
        setSaving(false);

        if (showErrorMessage) {
          showErrorMessage(t('ADMIN.INTEGRATIONS.MAPPING_SAVE_ERROR'));
        } else {
          setError(t('ADMIN.INTEGRATIONS.MAPPING_SAVE_ERROR'));
        }

        if (willSaveToken) {
          setExternalMappingLoadError(null);
          setExternalEntitiesRefreshKey((key) => key + 1);
        }

        return;
      }

      setSaving(false);

      if (onMappingsSaved) {
        onMappingsSaved({ mappings: currentMappings, partnerCode: integration.code });
      }

      if (showSuccessMessage) {
        showSuccessMessage(t('ADMIN.INTEGRATIONS.MAPPING_SAVE_SUCCESS'));
      }

      handleClose();
      return;
    }

    if (willSaveToken) {
      setExternalMappingLoadError(null);
      setExternalEntitiesRefreshKey((key) => key + 1);
    }
  }, [
    currentMappings,
    handleClose,
    integration,
    isMappingValid,
    isTokenAuth,
    onMappingsSaved,
    onTokenSaved,
    tokenFromApi,
    showErrorMessage,
    hasRequiredMappings,
    showSuccessMessage,
    storeId,
    t,
    tokenInput,
  ]);

  const handleActivate = useCallback(async () => {
    if (!integration || !canActivate || redirecting || tokenSaving || saving) {
      return;
    }

    setError(null);

    if (isTokenAuth) {
      await submitTokenCredential({ keepOpenAfter: true });
      return;
    }

    setRedirecting(true);

    try {
      const redirectUrl = await getOauth2RedirectUrl(integration.id, returnPath, storeId);

      if (redirectUrl && typeof redirectUrl === 'string') {
        window.location.href = redirectUrl;
        return;
      }

      setError(t('ADMIN.INTEGRATIONS.REDIRECT_ERROR'));
      setRedirecting(false);
    } catch {
      setError(t('ADMIN.INTEGRATIONS.REDIRECT_ERROR'));
      setRedirecting(false);
    }
  }, [
    canActivate,
    integration,
    isTokenAuth,
    redirecting,
    returnPath,
    saving,
    storeId,
    submitTokenCredential,
    t,
    tokenSaving,
  ]);

  const performIntegrationDisconnect = useCallback(async () => {
    if (!integration || (!isOAuth2 && !isTokenAuth) || disconnecting) {
      return;
    }

    setError(null);
    setDisconnecting(true);

    try {
      await disconnectIntegration(integration.id, storeId);

      setDisconnecting(false);

      if (onIntegrationDisconnected) {
        onIntegrationDisconnected();
      }
    } catch {
      setDisconnecting(false);

      if (showErrorMessage) {
        showErrorMessage(t('ADMIN.INTEGRATIONS.OAUTH_DISCONNECT_ERROR'));
        return;
      }

      setError(t('ADMIN.INTEGRATIONS.OAUTH_DISCONNECT_ERROR'));
    }
  }, [
    disconnecting,
    integration,
    isOAuth2,
    isTokenAuth,
    onIntegrationDisconnected,
    showErrorMessage,
    storeId,
    t,
  ]);

  const handleDeactivateClick = useCallback(() => {
    if (!integration || (!isOAuth2 && !isTokenAuth) || disconnecting || !openGenericModal) {
      return;
    }

    const warningModalConfig = getConfirmationModal({
      title: t('ADMIN.INTEGRATIONS.OAUTH_DISCONNECT_CONFIRM_TITLE'),
      content: t('ADMIN.INTEGRATIONS.OAUTH_DISCONNECT_CONFIRM_MESSAGE'),
      icon: '/images/inpulse/power-white-small.svg',
      actions: [
        GENERIC_MODAL_CANCEL_BUTTON(),
        {
          key: 1,
          color: 'inpulse-default',
          label: t('GENERAL.DEACTIVATE'),
          icon: '/images/inpulse/power-white-small.svg',
          handleClick: () => {
            performIntegrationDisconnect();
          },
        },
      ],
    });

    openGenericModal(warningModalConfig);
  }, [
    disconnecting,
    integration,
    isOAuth2,
    isTokenAuth,
    openGenericModal,
    performIntegrationDisconnect,
    t,
  ]);

  const handleMappingChange = useCallback(({ isValid, mappings }) => {
    setCurrentMappings(mappings);
    setIsMappingValid(isValid);
  }, []);

  const handleInitialMappingsLoaded = useCallback((initialMappings) => {
    setSavedMappingsSnapshot(initialMappings);
  }, []);

  const handleTokenInputChange = useCallback((e) => {
    setTokenInput(e.target.value);
  }, []);

  if (!integration) {
    return null;
  }

  const { iconColor, iconLetter, logoUrl, name, status } = integration;

  const initialMappingsForPartner =
    integration.code === PENNYLANE_OAUTH2_CODE ? prefetchedMappings : null;

  const showCentralKitchenHint =
    showMappingActivePanel && !isKitchenContext && integration.code === PENNYLANE_OAUTH2_CODE;

  // Keep `filter: none` even while disconnecting, otherwise the Button's default icon filter
  // re-tints the red power icon for the few seconds the request is in flight.
  const deactivateIconCustomStyle =
    isOAuth2 || isTokenAuth ? FOOTER_DEACTIVATE_ICON_STYLE : undefined;

  const externalEntityMappingPanelMountKey = `${integration.id}-${storeId ?? ''}-${
    integration.partnerConfigId ?? ''
  }-${externalEntitiesRefreshKey}`;

  const oauthDeactivateFooterButton = (
    <Button
      buttonCustomStyle={FOOTER_DEACTIVATE_BUTTON_STYLE}
      color="inpulse-outline"
      handleClick={handleDeactivateClick}
      icon="/images/inpulse/power-red-small.svg"
      iconCustomStyle={deactivateIconCustomStyle}
      isDisabled={(!isOAuth2 && !isTokenAuth) || disconnecting}
      label={t('GENERAL.DEACTIVATE')}
      iconOnLeft
    />
  );

  let footerButtons = null;

  if (showInfoOnly) {
    footerButtons = (
      <Button
        buttonCustomStyle={FOOTER_BUTTON_STYLE}
        color="inpulse-default"
        handleClick={handleClose}
        icon="/images/inpulse/close-white-small.svg"
        label={t('GENERAL.CLOSE')}
      />
    );
  } else if (isActive) {
    footerButtons = (
      <>
        {oauthDeactivateFooterButton}
        {showSaveButton ? (
          <Button
            buttonCustomStyle={FOOTER_BUTTON_STYLE}
            color="inpulse-default"
            handleClick={handleSave}
            icon="/images/inpulse/check-white-small.svg"
            isDisabled={saveButtonDisabled}
            label={t('GENERAL.SAVE')}
            iconOnLeft
          />
        ) : null}
      </>
    );
  } else if (isPaused) {
    footerButtons = (
      <>
        {oauthDeactivateFooterButton}
        <Button
          buttonCustomStyle={FOOTER_BUTTON_STYLE}
          color="inpulse-default"
          handleClick={handleActivate}
          icon="/images/inpulse/check-white-small.svg"
          isDisabled={!canActivate || redirecting || tokenSaving || saving}
          label={t('GENERAL.ACTIVATE')}
          iconOnLeft
        />
      </>
    );
  } else {
    footerButtons = (
      <>
        <Button
          buttonCustomStyle={FOOTER_BUTTON_STYLE}
          color="inpulse-outline"
          handleClick={handleClose}
          icon="/images/inpulse/close-black-small.svg"
          label={t('GENERAL.CANCEL')}
        />
        <Button
          buttonCustomStyle={FOOTER_BUTTON_STYLE}
          color="inpulse-default"
          handleClick={handleActivate}
          icon="/images/inpulse/check-white-small.svg"
          isDisabled={!canActivate || redirecting || tokenSaving || saving}
          label={t('GENERAL.ACTIVATE')}
        />
      </>
    );
  }

  return (
    <CustomDrawer customWidth="480px" isOpened onClick={handleClose}>
      <PanelRoot onClick={(e) => e.stopPropagation()}>
        <IntegrationDrawerHeader
          iconColor={iconColor}
          iconLetter={iconLetter}
          logoUrl={logoUrl}
          name={name}
          status={status}
          t={t}
          onClose={handleClose}
        />

        <Divider />

        {showOauthPausedFooterHint ? (
          <OauthFooterHintHost $hintPlacement="belowHeader">
            <OauthFooterHintBox $footerHintTone="warning">
              <OauthFooterHintText>
                {t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_OAUTH_PAUSED_FOOTER_HINT')}
              </OauthFooterHintText>
            </OauthFooterHintBox>
          </OauthFooterHintHost>
        ) : null}

        <BodyScroll>
          <IntegrationDescriptionContent
            integration={integration}
            isKitchenContext={isKitchenContext}
            isPaused={isPaused}
            showInfoOnly={showInfoOnly}
            t={t}
            tokenInput={tokenInput}
            onTokenChange={handleTokenInputChange}
          />
          {showMappingActivePanel && externalMappingLoadError ? (
            <FeedbackMessageHost>
              <Text color={ENUM_COLORS.INFO_RED} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                {externalMappingLoadError}
              </Text>
            </FeedbackMessageHost>
          ) : null}
          {showMappingActivePanel && !externalMappingLoadError ? (
            <MappingSectionHost>
              <ExternalEntityMappingPanel
                externalEntitiesRefreshKey={externalEntitiesRefreshKey}
                initialMappings={initialMappingsForPartner}
                initialStores={prefetchedStores}
                integrationCode={integrationCode}
                isKitchenContext={isKitchenContext}
                key={externalEntityMappingPanelMountKey}
                linkedStoreIds={linkedStoreIds}
                partnerConfigId={integration.partnerConfigId ?? null}
                partnerId={integration.id}
                partnerName={integration.name}
                requiredMappings={integration.requiredMappings}
                storeId={storeId}
                onExternalEntitiesError={setExternalMappingLoadError}
                onInitialMappingsLoaded={handleInitialMappingsLoaded}
                onMappingChange={handleMappingChange}
              />
            </MappingSectionHost>
          ) : null}
          {showPayrollSyncPanel ? (
            <PayrollSyncPanel
              partnerName={integration.name}
              showErrorMessage={showErrorMessage}
              showSuccessMessage={showSuccessMessage}
              storeIds={payrollSyncStoreIds}
              onSyncSuccess={handleClose}
            />
          ) : null}
          <DrawerBodyFeedback
            error={error}
            mappingSaving={saving}
            redirecting={redirecting}
            t={t}
            tokenSaving={tokenSaving}
          />
        </BodyScroll>

        {showCentralKitchenHint ? (
          <OauthFooterHintHost $hintPlacement="aboveFooter">
            <OauthFooterHintBox $footerHintTone="neutral">
              <OauthFooterHintText>
                {t('ADMIN.INTEGRATIONS.PENNYLANE_OAUTH2_OAUTH_ACTIVE_CENTRAL_KITCHEN_HINT')}
              </OauthFooterHintText>
            </OauthFooterHintBox>
          </OauthFooterHintHost>
        ) : null}

        <Divider />

        <Footer customStyle={FOOTER_CUSTOM_STYLE}>
          <ButtonsContainer>{footerButtons}</ButtonsContainer>
        </Footer>
      </PanelRoot>
    </CustomDrawer>
  );
};
